import { useEffect } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { Toaster } from './components/UI'
import { ErrorBoundary } from './components/ErrorBoundary'
import { Shell } from './components/Layout'
import { useAuth, useMarket } from './store'
import { get } from './lib/api'
import { bootstrapSeo } from './lib/seo'

import Home from './pages/Home'
import Markets from './pages/Markets'
import Pulse from './pages/Pulse'
import Pay from './pages/Pay'
import Trade from './pages/Trade'
import Convert from './pages/Convert'
import Auth from './pages/Auth'
import Dashboard from './pages/Dashboard'
import WalletPage from './pages/Wallet'
import CardsPage from './pages/Cards'
import AppDownload from './pages/AppDownload'
import TransferPage from './pages/Transfer'
import Crypto from './pages/Crypto'
import History from './pages/History'
import Kyc from './pages/Kyc'
import Security from './pages/Security'
import ApiKeys from './pages/ApiKeys'
import Merchant from './pages/Merchant'
import Merchants from './pages/Merchants'
import VerifyEmail from './pages/VerifyEmail'
import Alerts from './pages/Alerts'
import Referral from './pages/Referral'
import Support from './pages/Support'
import Privacy from './pages/Privacy'
import Staking from './pages/Staking'
import Blog from './pages/Blog'
import BlogPost from './pages/BlogPost'
import Info from './pages/Info'
import NotFound from './pages/NotFound'
import AdminApp from './admin/AdminApp'

function Guard({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth()
  const loc = useLocation()
  if (loading) {
    return (
      <div className="grid h-screen place-items-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-brand-500/30 border-t-brand-400" />
      </div>
    )
  }
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />
  return children
}

function ScrollTop() {
  const { pathname } = useLocation()
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })
    // اگر کشو یا مودالی هنگام جابه‌جایی باز مانده باشد، قفل اسکرول را آزاد می‌کند
    document.body.style.overflow = ''
  }, [pathname])
  return null
}

/**
 * مرز خطای هر مسیر.
 *
 * دو نکته که تفاوت بین «صفحه باز نمی‌شود مگر با رفرش» و بازیابی نرم را می‌سازند:
 *
 * ۱. پیام خطا **داخل** پوسته سایت نمایش داده می‌شود، نه به‌جای آن. اگر نوار
 *    بالا و منو هم ناپدید شوند، کاربر هیچ راهی جز رفرش کامل ندارد — و همین
 *    باعث می‌شد باگ به شکل «باید یک بار رفرش کنم» دیده شود.
 * ۲. بازنشانی با `resetKey` انجام می‌شود نه `key`؛ با `key`، هر جابه‌جایی کل
 *    درخت (نوار بالا، اعلان‌ها، اتصال زنده قیمت) را پیاده و دوباره سوار
 *    می‌کرد و هر بار یک پرش دیداری می‌ساخت.
 */
function RouteBoundary({ children }: { children: JSX.Element }) {
  const { pathname } = useLocation()
  const nav = useNavigate()
  return (
    <ErrorBoundary
      resetKey={pathname}
      onReset={() => nav(pathname, { replace: true })}
      frame={(node) => <Shell>{node}</Shell>}
    >
      {children}
    </ErrorBoundary>
  )
}

export default function App() {
  const init = useAuth((s) => s.init)
  const start = useMarket((s) => s.start)
  const stop = useMarket((s) => s.stop)

  useEffect(() => {
    init()
    start()
    // تأیید مالکیت گوگل، تحلیل ترافیک و داده ساختاریافته سازمان — از تنظیمات
    // مدیر خوانده می‌شود و اگر در دسترس نبود، بی‌صدا رد می‌شود.
    bootstrapSeo(() => get('/seo/config'))
    return () => stop()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <ScrollTop />
      <RouteBoundary>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/markets" element={<Markets />} />
          <Route path="/pulse" element={<Pulse />} />
          <Route path="/pay/:token" element={<Pay />} />
          <Route path="/merchants" element={<Merchants />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/trade/:symbol" element={<Trade />} />
          <Route path="/convert" element={<Convert />} />
          <Route path="/staking" element={<Staking />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/faq" element={<Info page="faq" />} />
          <Route path="/fees" element={<Info page="fees" />} />
          <Route path="/about" element={<Info page="about" />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/app" element={<AppDownload />} />
          <Route path="/login" element={<Auth mode="login" />} />
          <Route path="/register" element={<Auth mode="register" />} />

          <Route path="/dashboard" element={<Guard><Dashboard /></Guard>} />
          <Route path="/wallet" element={<Guard><WalletPage /></Guard>} />
          <Route path="/cards" element={<Guard><CardsPage /></Guard>} />
          <Route path="/crypto" element={<Guard><Crypto /></Guard>} />
          <Route path="/transfer" element={<Guard><TransferPage /></Guard>} />
          <Route path="/history" element={<Guard><History /></Guard>} />
          <Route path="/kyc" element={<Guard><Kyc /></Guard>} />
          <Route path="/security" element={<Guard><Security /></Guard>} />
          <Route path="/api-keys" element={<Guard><ApiKeys /></Guard>} />
          <Route path="/merchant" element={<Guard><Merchant /></Guard>} />
          <Route path="/alerts" element={<Guard><Alerts /></Guard>} />
          <Route path="/referral" element={<Guard><Referral /></Guard>} />
          <Route path="/support" element={<Guard><Support /></Guard>} />
          <Route path="/admin/*" element={<AdminApp />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </RouteBoundary>
      <Toaster />
    </>
  )
}
